import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { DEMO_WORKFLOW, MOCK_ENTRIES, MOOD_TYPES } from '../data/mockData';

const { FiActivity, FiEdit3, FiCpu, FiWifi, FiFileText, FiCheckCircle, FiLoader, FiAlertTriangle, FiInfo, FiLayers, FiFilter, FiChevronRight } = FiIcons;

const iconMap = {
  Activity: FiActivity,
  Edit3: FiEdit3,
  Cpu: FiCpu,
  Wifi: FiWifi,
  FileText: FiFileText
};

const Demo = () => {
  const [activeTab, setActiveTab] = useState('workflow');
  const [simulationState, setSimulationState] = useState('idle');

  const runSimulation = () => {
    setSimulationState('loading');
    setTimeout(() => setSimulationState('success'), 2000);
    setTimeout(() => setSimulationState('idle'), 4000);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-5xl mx-auto"
      >
        <header className="mb-10">
          <h1 className="text-3xl font-bold text-white mb-3">Product Demo & Systems</h1>
          <p className="text-gray-400 max-w-2xl">
            Explore the MoodGrid architecture. Designed for founders to track high-performance emotional patterns without clinical diagnostic claims.
          </p>
        </header>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 p-1 bg-ink-800/50 rounded-xl w-fit">
          {['Workflow', 'Sample Data', 'Simulated States'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab.toLowerCase().replace(' ', ''))}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.toLowerCase().replace(' ', '')
                  ? 'bg-ink-700 text-aurora shadow-sm'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'workflow' && (
            <motion.div
              key="workflow"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="md:col-span-2 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-200 mb-6 flex items-center gap-2">
                    <SafeIcon icon={FiLayers} className="w-5 h-5 text-aurora" />
                    Try this flow: The Founder Journey
                  </h3>
                  <div className="space-y-4">
                    {DEMO_WORKFLOW.map((item) => (
                      <div key={item.step} className="glass-panel p-5 rounded-2xl flex items-start gap-4 hover:border-ink-500 transition-colors cursor-pointer">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          item.status === 'completed' ? 'bg-aurora/10 text-aurora' : 
                          item.status === 'active' ? 'bg-lilac/20 text-lilac animate-pulse' : 'bg-ink-700 text-gray-500'
                        }`}>
                          <SafeIcon icon={iconMap[item.icon]} className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <h4 className="font-semibold text-gray-100">{item.title}</h4>
                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${
                              item.status === 'completed' ? 'bg-aurora/10 text-aurora' : 
                              item.status === 'active' ? 'bg-lilac/20 text-lilac' : 'bg-ink-700 text-gray-500'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
                        </div>
                        <SafeIcon icon={FiChevronRight} className="w-5 h-5 text-gray-700 mt-2" />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="glass-panel p-6 rounded-2xl border-l-4 border-l-aurora">
                    <h4 className="font-semibold text-white mb-2">Live Status</h4>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-3 h-3 bg-aurora rounded-full animate-ping" />
                      <span className="text-sm text-gray-300">Synchronizing bio-metrics...</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Uptime</span>
                        <span className="text-gray-300">99.98%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Latency</span>
                        <span className="text-gray-300">14ms</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-panel p-6 rounded-2xl bg-gradient-to-br from-ink-800 to-ink-900 border border-lilac/10">
                    <h4 className="font-semibold text-lilac mb-4 flex items-center gap-2">
                       <SafeIcon icon={FiInfo} className="w-4 h-4" />
                       Non-Medical Policy
                    </h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      MoodGrid is a self-reflection tool. It does not provide medical advice, diagnosis, or treatment. All insights are generated via pattern matching in your own text logs.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'sampledata' && (
            <motion.div
              key="sampledata"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-8"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
                  <SafeIcon icon={FiFilter} className="w-5 h-5 text-lilac" />
                  Schema & Records
                </h3>
                <div className="flex gap-2">
                  <button className="bg-ink-700 text-xs px-3 py-1.5 rounded-lg text-gray-300 border border-ink-600 hover:bg-ink-600 transition-colors">Export CSV</button>
                  <button className="bg-ink-700 text-xs px-3 py-1.5 rounded-lg text-gray-300 border border-ink-600 hover:bg-ink-600 transition-colors">Raw JSON</button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-ink-700">
                <table className="w-full text-left text-sm">
                  <thead className="bg-ink-800 text-gray-400 font-medium">
                    <tr>
                      <th className="p-4">Timestamp</th>
                      <th className="p-4">Mood State</th>
                      <th className="p-4">Content Preview</th>
                      <th className="p-4">Tags</th>
                      <th className="p-4">System ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-700/50">
                    {MOCK_ENTRIES.map((entry) => {
                      const mood = MOOD_TYPES.find(m => m.id === entry.mood);
                      return (
                        <tr key={entry.id} className="hover:bg-ink-800/30 transition-colors">
                          <td className="p-4 text-gray-500 font-mono text-xs">{new Date(entry.date).toLocaleDateString()}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-[10px] font-bold ${mood?.bgColor} ${mood?.color}`}>
                              {mood?.label.toUpperCase()}
                            </span>
                          </td>
                          <td className="p-4 text-gray-300 truncate max-w-[200px]">{entry.content}</td>
                          <td className="p-4">
                            <div className="flex gap-1">
                              {entry.tags.slice(0, 2).map(t => (
                                <span key={t} className="text-[10px] text-lilac opacity-70 bg-lilac/5 px-1.5 rounded border border-lilac/10">{t}</span>
                              ))}
                            </div>
                          </td>
                          <td className="p-4 text-gray-600 font-mono text-[10px]">MG-{entry.id}-XF</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'simulatedstates' && (
            <motion.div
              key="simulatedstates"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              {/* Simulation Controller */}
              <div className="glass-panel p-8 rounded-2xl flex flex-col items-center justify-center text-center">
                <h3 className="text-xl font-bold text-white mb-4">State Simulator</h3>
                <p className="text-sm text-gray-400 mb-8 max-w-xs">Observe how the UI responds to different data availability and network conditions.</p>
                <button 
                  onClick={runSimulation}
                  disabled={simulationState !== 'idle'}
                  className="px-8 py-3 bg-aurora text-ink-900 rounded-xl font-bold shadow-lg shadow-aurora/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                >
                  {simulationState === 'idle' ? 'Trigger AI Synthesis' : 'Simulating...'}
                </button>
              </div>

              {/* State Previews */}
              <div className="space-y-6">
                {/* Empty State */}
                <div className="border border-dashed border-ink-600 rounded-2xl p-6 bg-ink-900/40">
                  <span className="text-[10px] font-mono text-gray-600 uppercase mb-4 block tracking-widest">State: Empty</span>
                  <div className="flex flex-col items-center py-4">
                    <div className="w-12 h-12 rounded-full bg-ink-800 flex items-center justify-center mb-3">
                      <SafeIcon icon={FiEdit3} className="w-6 h-6 text-gray-600" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">No signals recorded yet.</p>
                  </div>
                </div>

                {/* Loading State */}
                <div className={`glass-panel rounded-2xl p-6 transition-all duration-500 ${simulationState === 'loading' ? 'border-aurora/50 ring-1 ring-aurora/20' : 'opacity-40 grayscale'}`}>
                  <span className="text-[10px] font-mono text-gray-600 uppercase mb-4 block tracking-widest">State: In Progress</span>
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiLoader} className="w-5 h-5 text-aurora animate-spin" />
                    <span className="text-sm text-gray-300">Extracting emotional resonance...</span>
                  </div>
                </div>

                {/* Success State */}
                <div className={`glass-panel rounded-2xl p-6 transition-all duration-500 ${simulationState === 'success' ? 'border-aurora/50 bg-aurora/5' : 'opacity-40 grayscale'}`}>
                  <span className="text-[10px] font-mono text-gray-600 uppercase mb-4 block tracking-widest">State: Success</span>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-aurora/20 flex items-center justify-center">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-aurora" />
                    </div>
                    <span className="text-sm text-gray-100 font-semibold">Narrative synthesized successfully.</span>
                  </div>
                </div>

                {/* Warning/Error State */}
                <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-red-500/50">
                  <span className="text-[10px] font-mono text-gray-600 uppercase mb-4 block tracking-widest">State: Warning / Sync Error</span>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-200 font-semibold">Signal Density Low</p>
                      <p className="text-xs text-gray-500 mt-1">You missed 3 reflection days. AI patterns may be less accurate this week.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <footer className="mt-20 py-10 border-t border-ink-800 text-center">
          <p className="text-xs text-gray-600 font-mono tracking-[0.2em] uppercase">
            Made by LingLink AI and DigLab AI
          </p>
        </footer>
      </motion.div>
    </div>
  );
};

export default Demo;